import { twMerge } from "tailwind-merge";
import { Skeleton } from "../../Skeleton";
import type { CellComponentProps, CellEmphasis } from "../DataTable.types";
import { getCellContainerClasses, getCellTextClasses, getSkeletonSize, resolveValue } from "./cellUtils";

/**
 * U+2013 EN DASH.
 *
 * A range takes an en dash, never a hyphen (too short to read as "to" between two
 * numbers) and never an em dash — the em dash is spoken for: it is the platform's
 * "no value" mark, and reusing it here would make an empty cell and a two-ended band
 * share a glyph.
 */
const EN_DASH = "\u2013";

/**
 * U+200A HAIR SPACE, one on each side of the dash.
 *
 * A full space reads as two separate numbers that happen to be adjacent; no space at
 * all reads as a single hyphenated token. The hair space is the design inventory's
 * call and it is what makes `68 – 74` read as one quantity.
 */
const HAIR_SPACE = "\u200A";

export interface RangeCellProps<T = any> extends CellComponentProps<T> {
  /** Low end of the band. */
  min?: number | ((row: T) => number | null);
  /** High end of the band. */
  max?: number | ((row: T) => number | null);
  /** Fraction digits, applied as both the minimum and the maximum, to both ends. */
  decimals?: number;
  /** Rendered once, after the pair. Omitted when the unit is not resolvable. */
  unit?: string;
  /** Shown when neither end is a finite number. */
  emptyText?: string;
  align?: "left" | "center" | "right";
  emphasis?: CellEmphasis;
  className?: string;
}

/** A finite number, or `undefined` for anything that cannot be one end of a band. */
function toFiniteEnd(raw: unknown): number | undefined {
  if (raw === null || raw === undefined || raw === "") return undefined;
  const num = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(num) ? num : undefined;
}

/**
 * RangeCell
 *
 * A two-ended band in one cell — a thermostat's setpoints, an operating window, a
 * min/max pair — rendered as `min – max` with the unit stated once at the end.
 *
 * ## Degradation
 *
 * Three cases, and none of them is "render half a band":
 *
 * - **Neither end** → `emptyText`, the em dash. The same mark every other cell uses
 *   for absence, so an empty range column looks like every other empty column rather
 *   than like a broken one.
 * - **One end** → the present end alone, with its unit (`68 °F`), never `68 – ` and
 *   never `68 – —`. A half-drawn band reads as a rendering bug; one number reads as
 *   one number. No caller's data needs this today, but a cell must not depend on that
 *   staying true.
 * - **`min > max`** → rendered exactly as given. A reversed band is a data bug, and
 *   silently swapping the ends is how a data bug becomes unfindable.
 */
export function RangeCell<T = any>({
  row,
  context,
  min,
  max,
  decimals = 0,
  unit,
  emptyText = "—",
  align = "right",
  emphasis = "normal",
  className,
}: RangeCellProps<T>) {
  const { isLoading } = context;

  if (isLoading) {
    return <Skeleton className={getSkeletonSize(emphasis)} />;
  }

  const low = toFiniteEnd(resolveValue(min, row));
  const high = toFiniteEnd(resolveValue(max, row));

  if (low === undefined && high === undefined) {
    return <div className={getCellContainerClasses(align, "text-text-secondary")}>{emptyText}</div>;
  }

  const format = (value: number) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);

  // Not `${low ?? high}` as a single expression: one of the two is `undefined` here
  // by construction, and the non-null assertion that would be needed to satisfy the
  // compiler hides which one.
  const band =
    low !== undefined && high !== undefined
      ? `${format(low)}${HAIR_SPACE}${EN_DASH}${HAIR_SPACE}${format(high)}`
      : format(low !== undefined ? low : (high as number));

  // `tabular-nums` first, so a caller-supplied `className` can still override it, and
  // so the two ends of the band — and the same column on the row above — line up.
  const containerClasses = getCellContainerClasses(
    align,
    twMerge("tabular-nums", getCellTextClasses(emphasis), className)
  );

  return (
    <div className={containerClasses}>
      <span>{band}</span>
      {unit && <span className="text-text-secondary">{unit}</span>}
    </div>
  );
}
